import { Fragment } from "react";
import { Outlet } from "react-router-dom";
import { RiFileCopyLine, RiExternalLinkLine } from "@remixicon/react";
import { Transition } from "@headlessui/react";

import Button from "components/Button";
import Card from "components/Card";
import CheckedComponent from "components/CheckedComponent";
import MaxWidthPage from "components/MaxWidthPage";
import Page from "components/Page/Page";
import Tag from "components/Tag";

import { useCopyToClipboard } from "hooks/useCopyToClipboard";

import { FAST_LINEAR_TRANSITION } from "misc/transitionProperties";

import { useUser } from "routes/dataroutes/UserData";

import { cx } from "utils";

import { ReactComponent as ClaudeIcon } from "assets/icons/claude-icon.svg";
import { ReactComponent as CursorIcon } from "assets/icons/cursor-ide-icon.svg";
import { ReactComponent as WindsurfIcon } from "assets/icons/windsurf-icon.svg";
import { ReactComponent as ClineIcon } from "assets/icons/cline-icon.svg";
import { ReactComponent as LlamaIndexIcon } from "assets/icons/llamaindex-icon.svg";
import { ReactComponent as N8nIcon } from "assets/icons/n8n-icon.svg";
import { ReactComponent as ZapierIcon } from "assets/icons/zapier-icon.svg";


const LLM_DOCS_BASE = "https://docs.scraperapi.com/integrations/llm-integrations";
const AUTOMATION_DOCS_BASE = "https://docs.scraperapi.com/integrations/automation-and-workflow-integrations";
const MCP_SERVER_URL = "https://mcp.scraperapi.com/mcp";

const AVAILABLE_FEATURES = [
  "Structured Data Endpoints",
  "Crawler Integration",
  "Full Scraping Toolkit",
];

interface ToolSetupOption {
  name: string;
  url: string;
  logo: React.ComponentType<{ className?: string }>;
}

const TOOL_SETUP_OPTIONS: ToolSetupOption[] = [
  {
    name: "Claude",
    url: `${LLM_DOCS_BASE}/mcp-server/claude-desktop`,
    logo: ClaudeIcon,
  },
  {
    name: "Cline",
    url: `${LLM_DOCS_BASE}/mcp-server/cline-as-vs-code-extension`,
    logo: ClineIcon,
  },
  {
    name: "Cursor",
    url: `${LLM_DOCS_BASE}/mcp-server/cursor`,
    logo: CursorIcon,
  },
  {
    name: "LlamaIndex",
    url: `${LLM_DOCS_BASE}/llamaindex-integration`,
    logo: LlamaIndexIcon,
  },
  {
    name: "n8n",
    url: `${AUTOMATION_DOCS_BASE}/n8n-integration`,
    logo: N8nIcon,
  },
  {
    name: "Windsurf",
    url: `${LLM_DOCS_BASE}/mcp-server/windsurf`,
    logo: WindsurfIcon,
  },
  {
    name: "Zapier",
    url: `${AUTOMATION_DOCS_BASE}/zapier-integration`,
    logo: ZapierIcon,
  },
];


function CopyableCommand({ command, showPrompt = true }: Readonly<{ command: string; showPrompt?: boolean }>) {
  const [copied, copyToClipboard] = useCopyToClipboard();
  const isMultiline = command.includes("\n");

  return (
    <button
      type="button"
      className="flex w-full text-left gap-x-2 bg-codeViewPurple px-4 py-3 cursor-pointer hover:bg-codeViewPurple/90 transition-colors"
      onClick={() => copyToClipboard(command)}
    >
      <pre className="flex-1 text-sm font-mono whitespace-pre-wrap">
        {isMultiline
          ? command.split("\n").map((line, i, arr) => (
              <Fragment key={i}>
                <span className={line.startsWith("#") ? "text-neutral-400" : "text-white"}>
                  {line}
                </span>
                {i < arr.length - 1 && "\n"}
              </Fragment>
            ))
          : <span className="text-white">{showPrompt ? "$ " : ""}{command}</span>
        }
      </pre>
      <div className="flex items-center gap-x-1 shrink-0 self-start">
        <Transition show={copied}>
          <div className={cx(FAST_LINEAR_TRANSITION, "transition data-[closed]:opacity-0 data-[closed]:scale-0 origin-right")}>
            <Tag label="Copied" subtle />
          </div>
        </Transition>
        <RiFileCopyLine className="w-4 h-4 text-neutral-400 hover:text-neutral-300" />
      </div>
    </button>
  );
}


export default function MCPPage() {
  const user = useUser();

  return (
    <MaxWidthPage fullWidth>
      <Page
        title="Start Using MCP"
        subtitle="Connect to live web data in seconds, copy your endpoint and begin extracting."
        browserTitle="MCP"
      >
        <div className="flex flex-col gap-y-6 pb-8">

          {/* What's Available */}
          <Card size="LG" borderless>
            <h2 className="font-medium text-lg">What's Available</h2>
            <div className="flex flex-col gap-y-4">
              {AVAILABLE_FEATURES.map((feature) => (
                <CheckedComponent key={feature} className="text-sm">
                  {feature}
                </CheckedComponent>
              ))}
            </div>
          </Card>

          {/* Quick Setup */}
          <Card size="LG" borderless>
            <div className="flex justify-center">
              <Button
                text="Configure MCP"
                href={`${LLM_DOCS_BASE}/mcp-server`}
                icon={{ Icon: RiExternalLinkLine, size: "w-4 h-4", position: "trailing" }}
                className="button button-primary"
              />
            </div>
            <p className="text-sm text-neutral-500 text-center">
              or quick setup with
            </p>
            <div className="flex flex-wrap items-center justify-center divide-x divide-neutral-200">
              {TOOL_SETUP_OPTIONS.map((tool) => (
                <Button
                  key={tool.name}
                  text={tool.name}
                  href={tool.url}
                  icon={{ Icon: tool.logo, size: "w-6 h-6" }}
                  className="text-neutral-700 font-medium hover:bg-slate-50"
                  size="SM"
                  centerAlign={false}
                />
              ))}
            </div>
          </Card>

          <Card title="MCP Connection Details" size="LG" borderless>
            <CopyableCommand showPrompt={false} command={`# Server URL\n${MCP_SERVER_URL}\n\n# Bearer Token (API Key)\n${user?.apiKey ?? ""}`} />
          </Card>

          <Card title="Skill Setup Guide" size="LG" borderless>
            <p className="text-sm text-neutral-500">
              Install the agent skill for intelligent tool selection:
            </p>
            <CopyableCommand command="npx skills add scraperapi/scraperapi-skills" />
          </Card>
        </div>
      </Page>

      <Outlet />
    </MaxWidthPage>
  );
}
